/**
 * The navigation half of utils/returnAfterAuth: record the return
 * (techpage_return_after_auth {source, new_account, via}), forget the entry
 * and hard-navigate to the page, so it renders again in its signed-in state.
 */
import { trackClick } from './analytics';
import { armReturnAfterAuth, clearReturnAfterAuth, returnHref, ReturnEntry, stripNextParam, withNext } from './returnAfterAuth';

let going = false;

/** Go back to the page. Returns false (and does nothing) when the path would
 *  leave this origin — the caller then carries on as if there were none. */
export function goBackAfterAuth(entry: ReturnEntry, opts: { newAccount: boolean; via?: string }): boolean {
  if (going) return true;
  const href = returnHref(entry.path, window.location.origin);
  clearReturnAfterAuth();
  if (!href) return false;
  going = true;
  try {
    trackClick('techpage_return_after_auth', {
      source: entry.source || '', new_account: !!opts.newAccount, via: opts.via || entry.via || '',
      path: entry.path.slice(0, 200),
    });
  } catch { /* never let analytics block the return */ }
  // A beat for the event to leave (visitorTracking flushes on pagehide too).
  window.setTimeout(() => { window.location.replace(href); }, 150);
  return true;
}

/** A new account from a public page: to the app at "/" for onboarding, with
 *  the page in ?next= (the stored entry says the rest). */
export function goToOnboardingThenBack(path: string): void {
  // Armed for this attempt: "/" honours the stored page only when it is.
  armReturnAfterAuth();
  window.location.replace(withNext('/', path));
}

/** Drop ?next= from the address bar (no navigation). */
export function stripNextFromAddress(): void {
  try {
    const search = stripNextParam(window.location.search);
    if (search === window.location.search) return;
    window.history.replaceState(window.history.state, '',
      window.location.pathname + search + window.location.hash);
  } catch { /* ignore */ }
}
